// FIELD WEATHER — one reusable weather surface for the whole platform.
// Uses the existing /weather/forecast data only; every metric is omitted when
// the API doesn't return it (no invented rainfall, wind or conditions).
import { Link } from "react-router-dom";
import { CloudRain, CloudSun, Cloud, Sun, CloudLightning, Snowflake, ChevronRight, Wind, Droplets } from "lucide-react";
import { useSiteForecast, WX_TEXT } from "@/lib/useWeather";

const wxIcon = (code) => {
  if (code == null) return Cloud;
  if (code <= 1) return Sun;
  if (code <= 3) return CloudSun;
  if (code <= 48) return Cloud;
  if (code <= 67) return CloudRain;
  if (code <= 77) return Snowflake;
  if (code <= 82) return CloudRain;
  return CloudLightning;
};

// Site-relevant flags derived strictly from the forecast values we have.
const siteFlags = (d) => {
  const out = [];
  if (d.rain_mm != null && d.rain_mm >= 10) out.push({ label: "Heavy rain", tone: "danger" });
  else if (d.rain_prob != null && d.rain_prob >= 60) out.push({ label: "Rain expected", tone: "info" });
  if (d.wind_kmh != null && d.wind_kmh >= 40) out.push({ label: "High wind", tone: "warn" });
  if (d.temp_min != null && d.temp_min <= 2) out.push({ label: "Frost risk", tone: "info" });
  if (d.temp_max != null && d.temp_max >= 30) out.push({ label: "Hot conditions", tone: "warn" });
  return out;
};

const TONE = {
  danger: "bg-red-500/15 text-red-400 border-red-500/30",
  warn: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  info: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

const dayName = (iso) => {
  try { return new Date(`${iso}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short" }); } catch { return ""; }
};

export function FieldWeather({ variant = "panel" }) {
  const { days, place } = useSiteForecast(5);
  if (!days?.length) return null;
  const today = days[0];
  const Icon = wxIcon(today.weather_code);
  const flags = siteFlags(today);

  if (variant === "strip") {
    return <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2 text-xs text-muted-foreground" data-testid="field-weather-strip">
      <span className="inline-flex items-center gap-2 font-medium text-foreground"><Icon className="w-4 h-4"/>{place || WX_TEXT(today.weather_code)}</span>
      <span className="text-sm font-semibold text-foreground">{today.temp_max != null ? `${Math.round(today.temp_max)}°` : "—"}</span>
      {today.rain_prob != null && <span className="inline-flex items-center gap-1.5"><Droplets className="w-3.5 h-3.5 text-blue-500" aria-hidden="true" />Rain {today.rain_prob}%</span>}
      {today.rain_mm != null && <span>{today.rain_mm} mm</span>}
      {today.wind_kmh != null && <span className="inline-flex items-center gap-1.5"><Wind className="w-3.5 h-3.5" aria-hidden="true" />Wind {Math.round(today.wind_kmh)} km/h</span>}
    </div>;
  }

  return (
    <div
      className="rounded-xl px-4 py-3.5 min-w-[300px] border shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
      style={{ background: "rgba(10, 15, 22, 0.7)", borderColor: "rgba(255,255,255,0.14)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      data-testid="field-weather-panel"
    >
      <div className="flex items-start gap-3.5">
        <Icon className="w-11 h-11 text-[#FFB020] shrink-0" strokeWidth={1.6} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[26px] font-extrabold text-white leading-none tabular-nums">
              {today.temp_max != null ? `${Math.round(today.temp_max)}°C` : "—"}
            </span>
            {place && <span className="text-[12.5px] font-semibold text-[#A7B0BD] truncate">{place}</span>}
          </div>
          <div className="text-[11.5px] text-[#A7B0BD] mt-1 truncate">{WX_TEXT(today.weather_code)}</div>
        </div>
      </div>
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="field-weather-flags">
          {flags.map((f) => (
            <span key={f.label} className={`px-2 py-0.5 rounded-md border text-[9.5px] font-bold uppercase tracking-wider ${TONE[f.tone]}`}>
              {f.label}
            </span>
          ))}
        </div>
      )}
      {days.length > 1 && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-start justify-between gap-1" data-testid="field-weather-forecast">
          {days.map((d) => {
            const DIcon = wxIcon(d.weather_code);
            return (
              <div key={d.date} className="text-center min-w-[38px]">
                <div className="text-[9px] font-bold uppercase tracking-wider text-[#738091]">{dayName(d.date)}</div>
                <DIcon className="w-4 h-4 text-[#FFB020] mx-auto my-1.5" strokeWidth={1.9} />
                <div className="text-[11.5px] font-bold text-white tabular-nums">
                  {d.temp_max != null ? `${Math.round(d.temp_max)}°` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Link to="/app/operations-map" className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#A7B0BD] hover:text-[#FF7A18] transition-colors" data-testid="field-weather-view-all">
        View full forecast <ChevronRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

export default FieldWeather;
